import { canEdit, canViewExpenses, upsertNote } from '../store.js';
import { toDayKey } from '../utils/day-key.js';
import { formatDateIT } from '../utils/format.js';
import { escapeHtml } from '../utils/dom.js';
import { authorChip } from '../utils/author.js';
import { resolveStopImage } from '../utils/stop-images.js';
import { resolveStopDescription } from '../utils/stop-descriptions.js';
import { resolveTripBases, resolveBaseMapsUrl } from '../utils/trip-bases.js';
import { getCurrentBase } from '../utils/current-base.js';
import { getTripTiming } from '../utils/trip-timing.js';
import { suggestExpenseFromStop } from '../utils/planned-cost.js';
import { openExpenseQuickAdd } from '../utils/expense-quick-add.js';
import { metisTipForDay } from '../utils/metis-tips.js';
import { bindDayScrollHandoff, bindDayTransition } from '../utils/day-scroll.js';
import { bindTextareaSaveButtons, renderTextareaSaveActions } from '../utils/textarea-save.js';
import { bindTripShell, getTripMainEl, renderTripShell } from './shell.js';

const expandedStorageKey = (tripId) => `metis-days-expanded:${tripId}`;

const MAPS_ICON = `<svg class="pill-action__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 21s7-4.5 7-11a7 7 0 10-14 0c0 6.5 7 11 7 11z" stroke="currentColor" stroke-width="1.75"/><circle cx="12" cy="10" r="2.25" stroke="currentColor" stroke-width="1.75"/></svg>`;

const ROUTE_ICON = `<svg class="pill-action__icon" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 6h6l2 3h8" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/><circle cx="6" cy="18" r="2" stroke="currentColor" stroke-width="1.75"/><circle cx="18" cy="18" r="2" stroke="currentColor" stroke-width="1.75"/></svg>`;

const STAY_ICON = `<svg class="card-day__stay-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 10.5 12 4l8 6.5V20a1 1 0 01-1 1h-5v-6H10v6H5a1 1 0 01-1-1v-9.5z" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/></svg>`;

function loadDayOpenStates(tripId) {
  try {
    const raw = sessionStorage.getItem(expandedStorageKey(tripId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveDayOpenState(tripId, dayKey) {
  const states = loadDayOpenStates(tripId);
  states[dayKey] = true;
  try {
    sessionStorage.setItem(expandedStorageKey(tripId), JSON.stringify(states));
  } catch {
    /* ignore */
  }
}

function resolveFocusDayKey(ctx, states = loadDayOpenStates(ctx.tripId)) {
  const timing = getTripTiming(ctx.trip);
  if (timing?.phase === 'active') {
    const hasToday = ctx.days.some((day) => toDayKey(day.date) === timing.todayKey);
    if (hasToday) return timing.todayKey;
  }
  if (timing?.phase === 'upcoming' && ctx.days[0]) {
    return toDayKey(ctx.days[0].date);
  }

  const lastViewed = [...ctx.days].reverse().find((day) => {
    const key = toDayKey(day.date);
    return Object.prototype.hasOwnProperty.call(states, key) && states[key];
  });
  if (lastViewed) return toDayKey(lastViewed.date);

  return ctx.days[0] ? toDayKey(ctx.days[0].date) : null;
}

function isDayOpen(dayKey, ctx, states) {
  return resolveFocusDayKey(ctx, states) === dayKey;
}

function scheduledTimeLabel(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

function dayTypeLabel(type) {
  const map = {
    nature: 'Natura',
    water: 'Laghi',
    city: 'Città',
    relax: 'Relax',
    transfer: 'Trasferimento',
  };
  return map[type] ?? type;
}

function pillContent(raw, icon) {
  const text = String(raw ?? '').replace(new RegExp(`^${icon}\\s*`), '').trim();
  return `<span class="pill__icon" aria-hidden="true">${icon}</span><span class="pill__text">${escapeHtml(text)}</span>`;
}

function findStopInCtx(ctx, dayKey, stopId) {
  const day = ctx.days.find((d) => toDayKey(d.date) === dayKey);
  return day?.stops?.find((s) => s.id === stopId) ?? null;
}

function renderMapsAction(mapsUrl) {
  if (!mapsUrl) return '';
  return `
    <a
      class="pill-action pill-action--maps"
      href="${escapeHtml(mapsUrl)}"
      target="_blank"
      rel="noopener noreferrer"
      title="Apri in Maps"
      aria-label="Apri in Maps"
    >${MAPS_ICON}</a>
  `;
}

function renderRouteAction(tripId, dayKey) {
  return `
    <a
      class="pill-action pill-action--route"
      href="#/trip/${escapeHtml(tripId)}/map/${escapeHtml(dayKey)}"
      title="Vedi percorso del giorno"
      aria-label="Vedi percorso del giorno"
    >${ROUTE_ICON}</a>
  `;
}

function renderExpenseAddAction(dayKey) {
  if (!canEdit()) return '';
  return `
    <button
      type="button"
      class="pill-action pill-action--expense"
      data-expense-extra
      data-day-key="${escapeHtml(dayKey)}"
      title="Aggiungi spesa extra"
      aria-label="Aggiungi spesa extra"
    >
      <span class="pill-action__plus" aria-hidden="true">+</span>
      <span class="pill-action__emoji" aria-hidden="true">💰</span>
    </button>
  `;
}

function renderPillRow(content) {
  if (!content.trim()) return '';
  return `<div class="pill-row">${content}</div>`;
}

function renderStop(stop, index, dayKey, { tripId, showExpenseAdd = false }) {
  const imageUrl = resolveStopImage(stop);
  const style = imageUrl
    ? `style="--stop-image: url('${imageUrl.replace(/'/g, '%27')}')"`
    : '';

  const rows = [];

  if (stop.car_time) {
    const carPill = `<span class="pill pill--car pill--grow">${pillContent(stop.car_time, '🚗')}</span>`;
    rows.push(renderPillRow(`${carPill}${renderMapsAction(stop.maps_url)}`));
  } else if (stop.maps_url) {
    rows.push(renderPillRow(`<span class="pill pill--grow pill--spacer" aria-hidden="true"></span>${renderMapsAction(stop.maps_url)}`));
  }

  if (stop.walk_info) {
    const walkPill = `<span class="pill pill--walk pill--grow">${pillContent(stop.walk_info, '🥾')}</span>`;
    rows.push(renderPillRow(`${walkPill}${renderRouteAction(tripId, dayKey)}`));
  }

  if (canViewExpenses() && (stop.cost_info || showExpenseAdd)) {
    let costCell = '';
    if (stop.cost_info) {
      const pillHtml = pillContent(stop.cost_info, '€');
      if (canEdit()) {
        costCell = `<button type="button" class="pill pill--cost pill--grow pill--actionable" data-expense-from-stop data-day-key="${escapeHtml(dayKey)}" data-stop-id="${escapeHtml(stop.id)}" title="Registra spesa prevista">${pillHtml}</button>`;
      } else {
        costCell = `<span class="pill pill--cost pill--grow">${pillHtml}</span>`;
      }
    } else if (showExpenseAdd) {
      costCell = `<span class="pill pill--grow pill--spacer" aria-hidden="true"></span>`;
    }
    rows.push(renderPillRow(`${costCell}${showExpenseAdd ? renderExpenseAddAction(dayKey) : ''}`));
  }

  const description = resolveStopDescription(stop);
  const placeSummary = stop.place_meta?.displayName ?? stop.placeMeta?.displayName;
  const scheduled = stop.scheduled_at
    ? `<span class="stop-item__time">🕐 ${escapeHtml(scheduledTimeLabel(stop.scheduled_at))}</span>`
    : '';

  return `
    <section class="stop-item${imageUrl ? ' stop-item--has-image' : ''}" ${style}>
      <div class="stop-item__overlay">
        <div class="stop-item__header">
          <span class="stop-item__index">${index + 1}</span>
          <h3 class="stop-item__name">${escapeHtml(stop.name)}</h3>
          ${scheduled}
        </div>
        ${placeSummary && placeSummary !== stop.name ? `<p class="stop-item__place-meta">${escapeHtml(placeSummary)}</p>` : ''}
        ${description ? `<p class="stop-item__description">${escapeHtml(description)}</p>` : ''}
        ${rows.length ? `<div class="stop-item__pill-rows">${rows.join('')}</div>` : ''}
        ${stop.warning ? `<p class="stop-item__warning"><span class="stop-item__warning-icon">⚠️</span>${escapeHtml(stop.warning)}</p>` : ''}
      </div>
    </section>
  `;
}

function resolveExpenseAddStopIndex(day) {
  const stops = day.stops ?? [];
  const withCost = stops.findIndex((s) => s.cost_info);
  if (withCost >= 0) return withCost;
  return stops.length > 0 ? stops.length - 1 : -1;
}

function renderStayMapsLink(base) {
  const url = resolveBaseMapsUrl(base);
  if (!url) return '';
  return `
    <a
      class="card-day__stay-link"
      href="${escapeHtml(url)}"
      target="_blank"
      rel="noopener noreferrer"
      title="Alloggio: ${escapeHtml(base.name)}"
      aria-label="Apri alloggio in Maps"
    >${STAY_ICON}</a>
  `;
}

function renderDayCard(day, note, members, tripId, isFocusDay, bases) {
  const dayKey = toDayKey(day.date);
  const chip = note ? authorChip(members, note.updated_by, note.updated_at) : '';
  const hasStops = (day.stops ?? []).length > 0;
  const expenseAddIdx = resolveExpenseAddStopIndex(day);
  const tip = metisTipForDay(day, dayKey);
  const stops = day.stops ?? [];
  const stayForDay = getCurrentBase(bases, dayKey);
  const quickLinks = [
    stayForDay ? renderStayMapsLink(stayForDay) : '',
    hasStops ? `<a class="card-day__map-link" href="#/trip/${escapeHtml(tripId)}/map/${escapeHtml(dayKey)}">Mappa</a>` : '',
  ].filter(Boolean).join('');

  const stopsHtml = stops
    .map((s, i) =>
      renderStop(s, i, dayKey, {
        tripId,
        showExpenseAdd: canEdit() && i === expenseAddIdx,
      }),
    )
    .join('');

  const orphanExpenseRow =
    canEdit() && expenseAddIdx < 0
      ? `<div class="card-day__orphan-expense">${renderPillRow(renderExpenseAddAction(dayKey))}</div>`
      : '';

  return `
    <article class="card-day card-day--snap card-day--${escapeHtml(day.day_type)}${isFocusDay ? ' card-day--focus' : ''}" id="day-${dayKey}" data-day-key="${dayKey}">
      <header class="card-day__hero">
        <div class="card-day__hero-top">
          <div class="card-day__meta">
            <time class="card-day__date" datetime="${dayKey}">${formatDateIT(dayKey)}</time>
            <span class="card-day__tag card-day__tag--${escapeHtml(day.day_type)}">${dayTypeLabel(day.day_type)}</span>
          </div>
          ${quickLinks ? `<div class="card-day__quick-links">${quickLinks}</div>` : ''}
        </div>
        <h2 class="card-day__title">${escapeHtml(day.title)}</h2>
        <aside class="metis-tip">
          <span class="metis-tip__avatar" aria-hidden="true">🧭</span>
          <p class="metis-tip__text">${escapeHtml(tip)}</p>
        </aside>
      </header>
      <div class="card-day__body">
        ${day.ritmo ? `
          <div class="card-day__ritmo-block">
            <p class="card-day__ritmo-label">Ritmo della giornata</p>
            <p class="card-day__ritmo">${escapeHtml(day.ritmo)}</p>
          </div>
        ` : ''}
        <div class="card-day__stops">
          ${stopsHtml || '<p class="card-day__empty-stops">Nessuna tappa programmata.</p>'}
          ${orphanExpenseRow}
        </div>
        <div class="card-day__note">
          <div class="card-day__note-header">
            <p class="card-day__note-label">Note</p>
            ${chip}
          </div>
          <textarea
            class="field-input field-textarea note-input"
            data-day-key="${dayKey}"
            rows="2"
            placeholder="Aggiungi una nota per questa giornata…"
            ${canEdit() ? '' : 'disabled'}
          >${escapeHtml(note?.body ?? '')}</textarea>
          ${canEdit() ? renderTextareaSaveActions(dayKey, 'Salva nota') : ''}
        </div>
        <p class="card-day__scroll-hint" aria-hidden="true">Fine giornata — scorri ancora per il successivo</p>
      </div>
    </article>
  `;
}

function renderDaysContent(ctx) {
  const notesByDay = new Map(ctx.live.notes.map((n) => [n.day_key, n]));
  const states = loadDayOpenStates(ctx.tripId);
  const bases = resolveTripBases(ctx.live);
  const cards = ctx.days
    .map((day) => {
      const dayKey = toDayKey(day.date);
      return renderDayCard(
        day,
        notesByDay.get(dayKey),
        ctx.live.members,
        ctx.tripId,
        isDayOpen(dayKey, ctx, states),
        bases,
      );
    })
    .join('');

  return `
    <div class="days-view">
      <div class="days-date-transition" id="days-date-transition" hidden aria-live="polite">
        <time></time>
      </div>
      <div class="days-snap">${cards}</div>
    </div>
  `;
}

function scrollToFocusDay(container, focusKey, tripId) {
  if (!focusKey) return;
  if (tripId) saveDayOpenState(tripId, focusKey);

  requestAnimationFrame(() => {
    const el = container.querySelector(`#day-${CSS.escape(focusKey)}`);
    el?.scrollIntoView({ block: 'start', behavior: 'auto' });
  });
}

function bindDaySnapObserver(container, tripId) {
  if (!('IntersectionObserver' in window)) return;

  const cards = container.querySelectorAll('.card-day--snap');
  if (!cards.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting && e.intersectionRatio >= 0.45)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      const dayKey = visible.target.dataset.dayKey;
      if (dayKey) saveDayOpenState(tripId, dayKey);
    },
    { root: container, threshold: [0.45, 0.6, 0.75] },
  );

  cards.forEach((card) => observer.observe(card));
}

function bindExpenseExtra(container, ctx) {
  container.querySelectorAll('[data-expense-extra]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!canEdit()) return;
      openExpenseQuickAdd({
        instanceId: ctx.instance.id,
        dayKey: btn.dataset.dayKey,
        label: '',
        amount: null,
        category: 'altro',
        costPreview: '',
      });
    });
  });
}

function bindExpenseFromStops(container, ctx) {
  container.querySelectorAll('[data-expense-from-stop]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!canEdit()) return;
      const dayKey = btn.dataset.dayKey;
      const stop = findStopInCtx(ctx, dayKey, btn.dataset.stopId);
      if (!stop) return;
      const suggestion = suggestExpenseFromStop(stop, dayKey);
      openExpenseQuickAdd({
        instanceId: ctx.instance.id,
        ...suggestion,
      });
    });
  });
}

function bindNotes(container, ctx) {
  bindTextareaSaveButtons(container, {
    canSave: () => canEdit(),
    onSave: async (dayKey) => {
      const textarea = container.querySelector(`.note-input[data-day-key="${CSS.escape(dayKey)}"]`);
      if (!textarea) return;
      await upsertNote(ctx.instance.id, dayKey, textarea.value.trim());
    },
  });
}

function bindDayView(container, ctx) {
  const main = getTripMainEl(container) ?? container;
  const focusKey = resolveFocusDayKey(ctx);

  bindDaySnapObserver(main, ctx.tripId);
  bindDayScrollHandoff(main);

  requestAnimationFrame(() => {
    scrollToFocusDay(main, focusKey, ctx.tripId);
    requestAnimationFrame(() => {
      bindDayTransition(main, main.querySelector('#days-date-transition'), { initialDayKey: focusKey });
    });
  });

  bindNotes(main, ctx);
  if (canViewExpenses()) {
    bindExpenseFromStops(main, ctx);
    bindExpenseExtra(main, ctx);
  }
}

function shellOpts(ctx) {
  return {
    members: ctx.live.members,
    bases: resolveTripBases(ctx.live),
  };
}

export function renderDayByDay(container, ctx) {
  container.innerHTML = renderTripShell({
    trip: ctx.trip,
    tripId: ctx.tripId,
    activeTab: 'days',
    mainHtml: renderDaysContent(ctx),
    ...shellOpts(ctx),
  });
  bindTripShell(container, {
    tripId: ctx.tripId,
    instanceId: ctx.instance.id,
    tripTitle: ctx.trip.title,
  });
  bindDayView(container, ctx);
}

export function updateDayByDayMain(container, ctx) {
  const main = getTripMainEl(container);
  if (!main) return;
  main.innerHTML = renderDaysContent(ctx);
  bindDayView(container, ctx);
}
